import { useTranslation } from "react-i18next";
import { stdObject } from "../../data/types/stdObject";
import { useMainContext } from "../../context/MainContext";
import { b64EncodeUnicode, sendData } from "../../services/sendData";
import { colors } from "../../data/colors";
import secureLocalStorage from "react-secure-storage";
import { useEffect, useRef, useState } from "react";
import { arrayToObject } from "../../utils/arrayToObject";

export default function useFilesManager({
  init,
  filesButtons,
  updateRoot,
  level,
}: stdObject) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    setVisor,
    setMsg,
    setMsg2,
    setMsg3,
    setMsg4,
    setModalData,
    setModalData2,
    setModalData3,
    setModalData4,
    closeMsg,
    logoutAction,
  } = useMainContext();
  const [folder, setFolder] = useState<string>(init);
  const [filesList, setFilesList] = useState<stdObject>({});

  useEffect(() => {
    setFilesList(
      Array.isArray(filesButtons)
        ? arrayToObject({
            key: "type",
            erase: ["type"],
            data: filesButtons,
          })
        : filesButtons,
    );
  }, []);

  const startSendData = async (
    endPoint: string,
    payload: stdObject,
    isToken = true,
  ) => await sendData({ endPoint, payload, isToken, logoutAction });

  const selectSetMsg = (
    text: string | boolean,
    data: stdObject | boolean = false,
  ) => {
    if (typeof text === "string") {
      switch (level) {
        case 2:
          setMsg2(text);
          break;
        case 3:
          setMsg3(text);
          break;
        case 4:
          setMsg4(text);
          break;
        default:
          setMsg(text);
          break;
      }
    }
    if (typeof data === "object") {
      switch (level) {
        case 2:
          setModalData2(data);
          break;
        case 3:
          setModalData3(data);
          break;
        case 4:
          setModalData4(data);
          break;
        default:
          setModalData(data);
          break;
      }
    }
  };

  const startUploading = async (event: stdObject) => {
    selectSetMsg("loading");
    const file = event.target.files[0];
    if (!file) {
      cleanFileInput();
      return;
    }

    try {
      const duplicateResponse: stdObject = await startSendData(
        "checkDuplicate",
        {
          type: folder,
          code: filesList[folder].code,
          name: file.name,
        },
      );
      console.log("duplicateResponse", duplicateResponse);
      if (duplicateResponse.success && duplicateResponse.status) {
        if (duplicateResponse.duplicate) {
          selectSetMsg("custom1", {
            title: t("p.fileAlreadyExists"),
            label: t("p.wantToRewrite"),
            buttons: [
              {
                label: t("p.rewrite"),
                onClick: () => uploadingFile(file),
                color: colors.orange,
              },
              {
                label: t("p.cancel"),
                onClick: cleanFileInput,
                color: colors.darkGrey,
              },
            ],
          });
        } else {
          uploadingFile(file);
        }
        return;
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }

    cleanFileInput();
  };

  const uploadingFile = async (file: any) => {
    const token = secureLocalStorage.getItem("token");
    if (!file || !token) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "formData",
      b64EncodeUnicode(
        JSON.stringify({
          token: String(token),
          type: folder,
          code: filesList[folder].code,
        }),
      ),
    );
    try {
      const formResponse: stdObject = await startSendData(
        "uploadFile",
        { form: formData },
        false,
      );
      console.log("formResponse", formResponse);
      if (formResponse.status && formResponse.success) {
        const lFiles = Object.values(formResponse.files);
        setFilesList((prev) => ({
          ...prev,
          [folder]: { ...prev[folder], list: lFiles },
        }));
        updateRoot({ lFiles, update: formResponse.update, value: 1 });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
    cleanFileInput();
  };

  const cleanFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    closeMsg(level);
  };

  const handleFile = async (name: string) => {
    selectSetMsg("loading");
    const index = name.lastIndexOf(".");
    console.log("dfd", filesList, folder);
    const payloadHandleFile = {
      type: folder,
      code: filesList[folder].code,
      name: name.substring(0, index),
      ext: name.substring(index + 1),
    };
    console.log("payloadHandleFile", payloadHandleFile);
    const responseHandleFile: stdObject = await sendData({
      endPoint: "callFile",
      payload: payloadHandleFile,
      isBlob: true,
      logoutAction,
    });
    console.log("responseHandleFile", responseHandleFile);
    closeMsg(level);
    const { success, content } = responseHandleFile;
    if (success && content) {
      const visorData = {
        open: true,
        type: payloadHandleFile.type,
        code: payloadHandleFile.code,
        name,
        ext: payloadHandleFile.ext,
        src: content,
        funcDeleteFile: (lFiles: string[], update: number) => {
          setFilesList((prev) => ({
            ...prev,
            [folder]: { ...prev[folder], list: lFiles },
          }));
          updateRoot({ lFiles, update: update, value: 0 });
        },
      };
      setVisor({ ...visorData });
    }
  };

  const refreshFiles = async () => {
    selectSetMsg("loading");
    try {
      const payloadRefreshFiles = {
        folders: Array.isArray(filesButtons)
          ? filesButtons.map((e: stdObject) => [e.type, e.code])
          : [[folder, filesList[folder].code]],
      };
      console.log("payloadRefreshFiles", payloadRefreshFiles);
      const refreshResponse: stdObject = await startSendData(
        "refreshFile",
        payloadRefreshFiles,
      );
      console.log("refreshResponse", refreshResponse);
      if (refreshResponse.success && refreshResponse.status) {
        setFilesList((prev: stdObject) => {
          const updated: stdObject = { ...prev };
          Object.keys(prev).forEach((e: string) => {
            updated[e] = {
              ...prev[e],
              list: Object.values(refreshResponse[e]) ?? [],
            };
          });
          return updated;
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
    closeMsg(level);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click(); // Abre el selector de archivos
  };

  return {
    t,
    folder,
    setFolder,
    filesList,
    fileInputRef,
    startUploading,
    handleFile,
    refreshFiles,
    handleButtonClick,
  };
}
